/* eslint-disable */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { setToken, clearToken } from '@/lib/tokenStore';
import { resetCloakOptIn } from '@/lib/cloakWriteGuard';

const AuthCtx = createContext(null);

const IMPERSONATE_KEY = 've_impersonate_role';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Impersonation state — only meaningful when realRole === 'system_admin'.
  // Persisted in localStorage so a page refresh keeps the preview alive.
  const [impersonatedRole, setImpersonatedRoleState] = useState(
    typeof window !== 'undefined' ? (localStorage.getItem(IMPERSONATE_KEY) || null) : null,
  );

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      // Server is source-of-truth for impersonation; reconcile local cache.
      if (data?.real_role === 'system_admin' && data?.effective_role !== 'system_admin') {
        localStorage.setItem(IMPERSONATE_KEY, data.effective_role);
        setImpersonatedRoleState(data.effective_role);
      } else if (data?.real_role !== 'system_admin') {
        // Non-admins can never impersonate — purge any stale flag.
        localStorage.removeItem(IMPERSONATE_KEY);
        setImpersonatedRoleState(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, remember_me = false) => {
    // v266 — password auth restored. Third param toggles the "Remember me
    // for 30 days" checkbox — backend issues a 30d cookie instead of 7d.
    const { data } = await api.post('/auth/login', { email, password, remember_me });
    setToken(data.token);
    localStorage.removeItem(IMPERSONATE_KEY);
    setImpersonatedRoleState(null);
    // Hydrate from /auth/me so derived flags (is_maker, is_cloaked, impersonating)
    // land in state immediately — /auth/login's user payload is the raw record.
    try {
      const { data: me } = await api.get('/auth/me');
      setUser(me);
      try { window.dispatchEvent(new CustomEvent('ve-auth-changed', { detail: { user: me } })); } catch { /* ignore */ }
      return me;
    } catch {
      setUser(data.user);
      try { window.dispatchEvent(new CustomEvent('ve-auth-changed', { detail: { user: data.user } })); } catch { /* ignore */ }
      return data.user;
    }
  };

  const signup = async (payload) => {
    const { data } = await api.post('/auth/signup', payload);
    setToken(data.token);
    setUser(data.user);
    try { window.dispatchEvent(new CustomEvent('ve-auth-changed', { detail: { user: data.user } })); } catch { /* ignore */ }
    return data.user;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await api.post('/auth/google/session', { session_id: sessionId });
    setToken(data.token);
    setUser(data.user);
    try { window.dispatchEvent(new CustomEvent('ve-auth-changed', { detail: { user: data.user } })); } catch { /* ignore */ }
    return data.user;
  };

  // Generic session-applier used by the magic-link landing page and any
  // future passwordless auth entrypoint. Mirrors signup/googleLogin
  // side-effects but accepts an already-issued token + user payload.
  const applySession = (token, userPayload) => {
    setToken(token);
    localStorage.removeItem(IMPERSONATE_KEY);
    setImpersonatedRoleState(null);
    setUser(userPayload);
    try { window.dispatchEvent(new CustomEvent('ve-auth-changed', { detail: { user: userPayload } })); } catch { /* ignore */ }
    // Hydrate from /me in the background so derived flags (is_maker etc.)
    // are accurate without blocking the redirect.
    checkAuth();
    return userPayload;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore — best-effort server signal */ }
    clearToken();
    localStorage.removeItem(IMPERSONATE_KEY);
    setImpersonatedRoleState(null);
    setUser(null);
    try { window.dispatchEvent(new CustomEvent('ve-auth-changed', { detail: { user: null } })); } catch { /* ignore */ }
  };

  // ---- Role impersonation (system_admin only) ----
  const setImpersonatedRole = useCallback(async (role) => {
    const realRole = user?.real_role || user?.role;
    if (realRole !== 'system_admin') return; // ignored silently for non-admins
    // ALWAYS reset the cloak-write opt-in on any cloak change — entering,
    // exiting, or switching from one role to another. The Maker must
    // re-opt-in for each fresh cloak session so a stale "don't ask
    // again" flag never leaks across roles or sessions.
    resetCloakOptIn();
    if (!role || role === 'system_admin') {
      // Exit impersonation
      localStorage.removeItem(IMPERSONATE_KEY);
      setImpersonatedRoleState(null);
      try {
        await api.post('/auth/impersonate', { target_role: 'system_admin' });
      } catch { /* audit-only — non-blocking */ }
    } else {
      localStorage.setItem(IMPERSONATE_KEY, role);
      setImpersonatedRoleState(role);
      try {
        await api.post('/auth/impersonate', { target_role: role });
      } catch { /* audit-only */ }
    }
    await checkAuth();
  }, [user, checkAuth]);

  const realRole = user?.real_role || user?.role || null;
  const effectiveRole = impersonatedRole || user?.effective_role || user?.role || null;
  const isImpersonating = !!(impersonatedRole && realRole === 'system_admin');

  return (
    <AuthCtx.Provider value={{
      user,
      loading,
      login,
      signup,
      logout,
      googleLogin,
      exchangeGoogleSession,
      applySession,
      refresh: checkAuth,
      // Impersonation API
      realRole,
      effectiveRole,
      isImpersonating,
      impersonatedRole,
      setImpersonatedRole,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
